/**
 * A ticket's attachments — Step 55.
 *
 * Add and remove, with the size cap stated BEFORE anything is chosen. Telling
 * somebody a 40 MB file was too big after they have waited for it to upload is
 * the rude way round, and it is the mistake `FileFieldInput` already learned;
 * this reuses the same reasoning rather than the component, because that one
 * is bound to a custom field's value and this owns rows in `attachments`.
 *
 * Removing takes the ROW, never the stored object: storage is
 * content-addressed, so identical bytes are one file shared by every owner
 * that uploaded them, and deleting the object would pull a screenshot out
 * from under a KB article using the same image.
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Paperclip, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { fullTime } from '../../lib/time';

interface Row {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  migrated: number;
  createdAt: number;
}

/**
 * The `attachment` purpose's ceiling, so the control can say it BEFORE a file
 * is chosen.
 *
 * Must match `PURPOSES.attachment.maxBytes` in `lib/uploadSafety.ts`, which is
 * the authority and refuses with the real number. I first wrote 25 here from
 * memory and it is 15 — exactly the "hard-coded number that lies on the
 * client" that file's own comment warns about. A wrong-high value is the bad
 * direction: it invites somebody to wait through an upload that was always
 * going to be refused.
 */
const MAX_MB = 15;

const readable = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

export function TicketAttachments({ ticketId, canEdit }: { ticketId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const list = useQuery({
    queryKey: qk.tickets.attachments(ticketId),
    queryFn: () => apiFetch<{ entries: Row[] }>(`/api/tickets/${ticketId}/attachments`),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiSend(`/api/tickets/${ticketId}/attachments/${id}`, 'DELETE'),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not remove it.');
      void qc.invalidateQueries({ queryKey: qk.tickets.attachments(ticketId) });
      void qc.invalidateQueries({ queryKey: qk.tickets.activity(ticketId) });
    },
  });

  async function upload(files: FileList) {
    setBusy(true);
    for (const file of Array.from(files)) {
      // Checked here so the answer arrives before the wait, not after it.
      if (file.size > MAX_MB * 1024 * 1024) {
        toast.error(`${file.name} is larger than ${MAX_MB} MB.`);
        continue;
      }
      const form = new FormData();
      form.append('file', file);
      form.append('purpose', 'attachment');
      form.append('ownerType', 'ticket');
      form.append('ownerId', ticketId);
      // Bare fetch on purpose: apiSend serialises JSON, and a multipart body
      // must reach the network untouched so the browser sets its own boundary.
      const res = await fetch('/api/media', { method: 'POST', body: form, credentials: 'same-origin' });
      const body = await res.json().catch(() => null);
      if (!res.ok) toast.error(body?.error ?? `Could not upload ${file.name}.`);
    }
    setBusy(false);
    if (input.current) input.current.value = '';
    void qc.invalidateQueries({ queryKey: qk.tickets.attachments(ticketId) });
  }

  const rows = list.data?.entries ?? [];

  return (
    <div className="space-y-2">
      {rows.map((a) => (
        <div key={a.id} className="flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm">
          <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          {a.migrated ? (
            <a
              href={`/api/media/${a.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 truncate text-brand hover:underline"
            >
              {a.filename}
            </a>
          ) : (
            // A migration gap reads as a known gap, never as a mystery — the
            // plan's rule, and the reason `migrated` exists.
            <span className="min-w-0 flex-1 truncate text-muted-foreground">{a.filename} — not migrated</span>
          )}
          <span className="shrink-0 text-xs text-muted-foreground" title={fullTime(a.createdAt)}>
            {readable(a.size)}
          </span>
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              aria-label={`Remove ${a.filename}`}
              disabled={remove.isPending}
              onClick={() => remove.mutate(a.id)}
            >
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          )}
        </div>
      ))}

      {canEdit && (
        <div className="flex items-center gap-2">
          <input
            ref={input}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => e.target.files && upload(e.target.files)}
          />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => input.current?.click()}>
            <Paperclip className="h-3.5 w-3.5" />
            {busy ? 'Uploading…' : 'Attach files'}
          </Button>
          <span className="text-[11px] text-muted-foreground">Up to {MAX_MB} MB each.</span>
        </div>
      )}
      {!canEdit && rows.length === 0 && <p className="text-xs text-muted-foreground">Nothing attached.</p>}
    </div>
  );
}
